import { useEffect } from 'react';
import { Alert, App, Button, Card, Form, Input, Typography } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageBody, PageHeader } from '../../app/AppLayout';
import { ErrorState, LoadingState } from '../../components/PageStates';
import { ApiError, NetworkError } from '../../api/client';
import { tokens } from '../../theme';
import { appLinksApi, appLinksKeys } from './api';

interface FormValues {
  androidUrl: string;
  iosUrl: string;
  reason: string;
}

function blankToNull(value: string | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed.length === 0 ? null : trimmed;
}

export function AppLinksPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<FormValues>();

  const linksQuery = useQuery({
    queryKey: appLinksKeys.all,
    queryFn: appLinksApi.get,
  });

  useEffect(() => {
    if (!linksQuery.data) return;
    form.setFieldsValue({
      androidUrl: linksQuery.data.androidUrl ?? '',
      iosUrl: linksQuery.data.iosUrl ?? '',
      reason: '',
    });
  }, [linksQuery.data, form]);

  const saveMutation = useMutation({
    mutationFn: appLinksApi.update,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: appLinksKeys.all });
      form.setFieldValue('reason', '');
      message.success('Đã lưu. Ứng dụng thành viên đọc link mới ở lần mở kế tiếp.');
    },
    onError: (error) => {
      message.error(
        error instanceof ApiError || error instanceof NetworkError
          ? error.message
          : 'Không lưu được thay đổi.',
      );
    },
  });

  return (
    <>
      <PageHeader title="Link cửa hàng ứng dụng" subtitle="Cấu hình" />
      <PageBody>
        <Typography.Paragraph
          type="secondary"
          style={{ marginTop: 0, marginBottom: tokens.space.sectionGap }}
        >
          Khách bấm <b>Chia sẻ ứng dụng</b> trên trang Cá nhân sẽ gửi một trong hai link này qua Zalo,
          Messenger, tin nhắn hoặc app khác trên máy. Để trống một ô thì lựa chọn đó không hiện.
        </Typography.Paragraph>

        {linksQuery.isPending && <LoadingState />}
        {linksQuery.isError && (
          <ErrorState error={linksQuery.error} onRetry={() => void linksQuery.refetch()} />
        )}
        {linksQuery.isSuccess && (
          <Card>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Chỉ nhận địa chỉ https của Google Play hoặc App Store. Link khác bị từ chối."
            />
            <Form
              form={form}
              layout="vertical"
              requiredMark={false}
              onFinish={(values) =>
                saveMutation.mutate({
                  androidUrl: blankToNull(values.androidUrl),
                  iosUrl: blankToNull(values.iosUrl),
                  reason: values.reason.trim(),
                })
              }
            >
              <Form.Item
                name="androidUrl"
                label="Google Play"
                rules={[
                  {
                    validator: async (_, value: string | undefined) => {
                      const trimmed = value?.trim() ?? '';
                      if (!trimmed) return;
                      let url: URL;
                      try {
                        url = new URL(trimmed);
                      } catch {
                        throw new Error('Link Google Play không hợp lệ.');
                      }
                      if (url.protocol !== 'https:' || url.hostname !== 'play.google.com') {
                        throw new Error(
                          'Link Google Play phải bắt đầu bằng https://play.google.com/…',
                        );
                      }
                    },
                  },
                ]}
              >
                <Input
                  placeholder="https://play.google.com/store/apps/details?id=…"
                  maxLength={500}
                  allowClear
                />
              </Form.Item>

              <Form.Item
                name="iosUrl"
                label="App Store"
                rules={[
                  {
                    validator: async (_, value: string | undefined) => {
                      const trimmed = value?.trim() ?? '';
                      if (!trimmed) return;
                      let url: URL;
                      try {
                        url = new URL(trimmed);
                      } catch {
                        throw new Error('Link App Store không hợp lệ.');
                      }
                      if (url.protocol !== 'https:' || url.hostname !== 'apps.apple.com') {
                        throw new Error(
                          'Link App Store phải bắt đầu bằng https://apps.apple.com/…',
                        );
                      }
                    },
                  },
                ]}
              >
                <Input
                  placeholder="https://apps.apple.com/vn/app/…/id…"
                  maxLength={500}
                  allowClear
                />
              </Form.Item>

              <Form.Item
                name="reason"
                label="Lý do thay đổi"
                rules={[
                  { required: true, whitespace: true, message: 'Chưa nhập lý do thay đổi.' },
                  { max: 500, message: 'Lý do quá dài (tối đa 500 ký tự).' },
                ]}
              >
                <Input.TextArea
                  rows={2}
                  maxLength={500}
                  showCount
                  placeholder="Ví dụ: đổi sang bản listing mới sau khi đổi tên app"
                />
              </Form.Item>

              <Button type="primary" htmlType="submit" loading={saveMutation.isPending}>
                Lưu
              </Button>
            </Form>
          </Card>
        )}
      </PageBody>
    </>
  );
}
